import type { ReactNode } from 'react';
import { MagicWandIcon, TrayArrowDownIcon } from '@phosphor-icons/react';
import { Num, T, Var, useGT } from 'gt-react';
import type {
  WorkbookSinkMapping,
  WorkbookSinkOutput as SinkOutput,
  WorkbookSinkValue,
} from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import { useOutputEditor, type OutputEditor } from './useOutputEditor';
import {
  useDeliverWorkbookSink,
  useWorkbookSinkTargetColumns,
  useWorkbookSinkTargets,
} from './useWorkbookSinkTargets';
import { WorkbookOutputStatus } from './WorkbookFileOutput';
import {
  autoMapped,
  mappedCount,
  mappingFor,
  sinkIssues,
  sinkWritable,
  sinkSheetColumns,
  findSinkTarget,
  sinkTargetsOfKind,
  targetFromRef,
  targetKey,
  targetRef,
  withMapping,
  type SinkIssue,
  type SinkTarget,
  type SinkWriteMode,
  type SinkTargetColumn,
} from './workbookSinks';

type SinkEditor = OutputEditor;

const LINK_KIND = 'data-source-link';
const TABLE_KIND = 'stateful-table';
const UNMAPPED = 'unmapped';
const CONSTANT = 'constant';
const COLUMN_PREFIX = 'column:';

/** A mapping's value as the picker holds it. */
function pickerValue(value: WorkbookSinkValue | undefined): string {
  if (!value) return UNMAPPED;
  return value.kind === 'constant' ? CONSTANT : COLUMN_PREFIX + value.column;
}

/** Saves, then writes the sink now and says in a toast what it did. */
function useWriteNow(editor: SinkEditor, outputId: string) {
  const gt = useGT();
  const deliver = useDeliverWorkbookSink();
  async function run() {
    try {
      await editor.save();
      const sent = await deliver.mutateAsync({
        workbookId: editor.workbookId,
        outputId,
      });
      const summary = gt('Rows written: {written}. Failed: {failed}.', {
        context: 'workbook data sink write result toast',
        written: String(sent.rowCount),
        failed: String(sent.failedCount),
      });
      const detail = sent.issues.map((issue) => issue.message).join('\n');
      // Any issue but rows-failed means nothing was written.
      if (sent.issues.some((issue) => issue.code !== 'rows-failed'))
        toast.error(
          gt('Nothing was written.', {
            context: 'workbook data sink blocked write toast',
          }),
          { description: detail || undefined },
        );
      else if (sent.failedCount > 0 || sent.issues.length > 0)
        toast.warning(summary, { description: detail || undefined });
      else toast.success(summary);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not write the rows.', {
              context: 'workbook data sink write error toast',
            }),
      );
    }
  }
  return { run: () => void run(), pending: deliver.isPending };
}

/**
 * A Data sink Output: the Worksheet whose Result rows it writes, the Data
 * Source Link table they land in, and for each target column where its value
 * comes from: a Worksheet column or a constant. It writes after each Run when
 * asked to, and on demand.
 */
export function WorkbookSinkOutput({
  editor,
  output,
  busy,
  runMenu,
}: {
  editor: SinkEditor;
  output: SinkOutput;
  busy: boolean;
  /** Runs up to the Worksheet it writes; absent without Execute. */
  runMenu?: ReactNode;
}) {
  const gt = useGT();
  const { editable, definition, results, state } = useOutputEditor(
    editor,
    output,
    busy,
  );
  const targets = useWorkbookSinkTargets();
  const write = useWriteNow(editor, output.id);
  const target = findSinkTarget(targets.data ?? [], output.target);
  const columnsQuery = useWorkbookSinkTargetColumns(target);
  const targetColumns = columnsQuery.data;
  const sheetColumns = sinkSheetColumns(
    definition,
    output.sheetId,
    results.sheet(output.sheetId ?? null).result?.columns,
  );
  const update = (fn: (latest: SinkOutput) => SinkOutput) =>
    editor.edits
      .output(output.id)
      .update((latest) => (latest.kind === 'sink' ? fn(latest) : latest));
  const issues = targetColumns
    ? sinkIssues(output.mappings, targetColumns, sheetColumns)
    : [];

  const canWrite =
    editable &&
    !write.pending &&
    sinkWritable({
      state,
      targetColumns,
      mappingCount: output.mappings.length,
      issues,
    });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div
        className="flex flex-wrap items-end gap-3"
        role="group"
        aria-label={gt('Data sink controls', {
          context: 'workbook data sink output',
        })}
      >
        <SinkSourceFields
          output={output}
          sheets={definition.sheets}
          targets={targets.data ?? []}
          targetsPending={targets.isPending}
          editable={editable}
          update={update}
        />
        <div className="ml-auto flex items-center gap-1.5">
          {runMenu}
          <Button disabled={!canWrite} onClick={write.run}>
            {write.pending ? <Spinner /> : <TrayArrowDownIcon />}
            <T context="workbook data sink write now button">Write Now</T>
          </Button>
        </div>
      </div>
      <SinkWriteModeHint target={output.target} />
      <SinkLoadErrors
        columnsFailed={columnsQuery.isError}
        targetsFailed={targets.isError}
      />
      <label className="flex items-center gap-2 text-sm">
        <Switch
          checked={output.deliverOnRun}
          disabled={!editable}
          onCheckedChange={(deliverOnRun) =>
            update((latest) => ({ ...latest, deliverOnRun }))
          }
        />
        <T context="workbook data sink deliver on run switch">
          Write after each run
        </T>
      </label>
      <WorkbookOutputStatus state={state} />
      {target && targetColumns ? (
        <SinkMappings
          targetColumns={targetColumns}
          mappings={output.mappings}
          sheetColumns={sheetColumns}
          issues={issues}
          editable={editable}
          change={(mappings) => update((latest) => ({ ...latest, mappings }))}
          hasSheet={output.sheetId !== undefined}
        />
      ) : (
        <SinkNoTargetHint show={!targets.isPending && !target} />
      )}
    </div>
  );
}

function SinkLoadErrors({
  columnsFailed,
  targetsFailed,
}: {
  columnsFailed: boolean;
  targetsFailed: boolean;
}) {
  return (
    <>
      {columnsFailed && (
        <p role="alert" className="text-xs text-destructive">
          <T context="workbook data sink columns error">
            Could not load the columns of the target table.
          </T>
        </p>
      )}
      {targetsFailed && (
        <p role="alert" className="text-xs text-destructive">
          <T context="workbook data sink targets error">
            Could not load the tables a data sink can write.
          </T>
        </p>
      )}
    </>
  );
}

function SinkNoTargetHint({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="text-sm text-muted-foreground">
      <T context="workbook data sink no target hint">
        Choose a table to map this worksheet's columns onto.
      </T>
    </p>
  );
}

function SinkSourceFields({
  output,
  sheets,
  targets,
  targetsPending,
  editable,
  update,
}: {
  output: SinkOutput;
  sheets: readonly { id: string; name: string }[];
  targets: readonly SinkTarget[];
  targetsPending: boolean;
  editable: boolean;
  update: (fn: (latest: SinkOutput) => SinkOutput) => void;
}) {
  const gt = useGT();
  return (
    <>
      <Field label={gt('Worksheet', { context: 'workbook data sink field' })}>
        <Select
          value={output.sheetId ?? null}
          disabled={!editable}
          onValueChange={(sheetId) =>
            update((latest) => ({ ...latest, sheetId: sheetId ?? undefined }))
          }
        >
          <SelectTrigger
            className="min-w-48"
            aria-label={gt('Worksheet to write', {
              context: 'workbook data sink worksheet selector',
            })}
          >
            <SelectValue
              placeholder={gt('Choose a worksheet', {
                context: 'workbook data sink worksheet placeholder',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {sheets.map((sheet) => (
              <SelectItem key={sheet.id} value={sheet.id}>
                {sheet.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <SinkTargetFields
        output={output}
        targets={targets}
        targetsPending={targetsPending}
        editable={editable}
        update={update}
      />
    </>
  );
}

function SinkTargetFields({
  output,
  targets,
  targetsPending,
  editable,
  update,
}: {
  output: SinkOutput;
  targets: readonly SinkTarget[];
  targetsPending: boolean;
  editable: boolean;
  update: (fn: (latest: SinkOutput) => SinkOutput) => void;
}) {
  const gt = useGT();
  return (
    <>
      <Field label={gt('Write to', { context: 'workbook data sink field' })}>
        <Select
          value={output.target ? targetKey(targetRef(output.target)) : null}
          disabled={!editable || targetsPending}
          onValueChange={(key) => {
            const chosen = targets.find((item) => targetKey(item) === key);
            update((latest) => ({
              ...latest,
              target: chosen ? targetFromRef(chosen) : undefined,
              // Mappings name the old table's columns.
              mappings: [],
            }));
          }}
        >
          <SelectTrigger
            className="min-w-56"
            aria-label={gt('Table to write', {
              context: 'workbook data sink target selector',
            })}
          >
            <SelectValue
              placeholder={gt('Choose a table', {
                context: 'workbook data sink target placeholder',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {sinkTargetGroup(
              gt('Data sinks', {
                context: 'workbook data sink target group',
              }),
              sinkTargetsOfKind(targets, LINK_KIND),
            )}
            {sinkTargetGroup(
              gt('Stateful tables', {
                context: 'workbook data sink target group',
              }),
              sinkTargetsOfKind(targets, TABLE_KIND),
            )}
          </SelectContent>
        </Select>
      </Field>
      {output.target?.kind === TABLE_KIND && (
        <SinkWriteModeField
          writeMode={output.target.writeMode}
          editable={editable}
          change={(writeMode) =>
            update((latest) =>
              latest.target?.kind === TABLE_KIND
                ? { ...latest, target: { ...latest.target, writeMode } }
                : latest,
            )
          }
        />
      )}
    </>
  );
}

/** Called, not rendered as an element: the Select reads its items from its children. */
function sinkTargetGroup(label: string, items: readonly SinkTarget[]) {
  if (!items.length) return null;
  return (
    <SelectGroup>
      <SelectLabel>{label}</SelectLabel>
      {items.map((item) => (
        <SelectItem key={targetKey(item)} value={targetKey(item)}>
          {item.label}
          <span className="ml-2 font-mono text-xs text-muted-foreground">
            {item.detail}
          </span>
        </SelectItem>
      ))}
    </SelectGroup>
  );
}

/** How a Stateful table takes the rows: merged by key, or replaced wholesale. */
function SinkWriteModeField({
  writeMode,
  editable,
  change,
}: {
  writeMode: SinkWriteMode;
  editable: boolean;
  change: (writeMode: SinkWriteMode) => void;
}) {
  const gt = useGT();
  return (
    <Field label={gt('Write mode', { context: 'workbook data sink field' })}>
      <Select
        value={writeMode}
        disabled={!editable}
        onValueChange={(next) => {
          if (next === 'snapshot' || next === 'upsert') change(next);
        }}
      >
        <SelectTrigger
          className="min-w-44"
          aria-label={gt('Write mode for the table', {
            context: 'workbook data sink write mode selector',
          })}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="upsert">
            {gt('Merge by key', {
              context: 'workbook data sink write mode option',
            })}
          </SelectItem>
          <SelectItem value="snapshot">
            {gt('Replace table', {
              context: 'workbook data sink write mode option',
            })}
          </SelectItem>
        </SelectContent>
      </Select>
    </Field>
  );
}

/** What a Stateful table's write mode does, under the controls so it never shifts them. */
function SinkWriteModeHint({ target }: { target: SinkOutput['target'] }) {
  const gt = useGT();
  if (target?.kind !== TABLE_KIND) return null;
  return (
    <p className="-mt-2 text-xs text-muted-foreground">
      {target.writeMode === 'snapshot'
        ? gt(
            'Replace table: rows not written by this sink are removed from the table.',
            {
              context: 'workbook data sink replace table hint',
            },
          )
        : gt(
            'Merge by key: rows are added, or updated where the key matches.',
            {
              context: 'workbook data sink merge by key hint',
            },
          )}
    </p>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

/** One row per target column, each with a picker for where its value comes from. */
export function SinkMappings({
  targetColumns,
  mappings,
  sheetColumns,
  issues,
  editable,
  hasSheet,
  change,
}: {
  targetColumns: readonly SinkTargetColumn[];
  mappings: readonly WorkbookSinkMapping[];
  sheetColumns: readonly string[];
  issues: readonly SinkIssue[];
  editable: boolean;
  hasSheet: boolean;
  change: (mappings: WorkbookSinkMapping[]) => void;
}) {
  const mapped = mappedCount(mappings, targetColumns);
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-medium">
          <T context="workbook data sink mappings heading">Column mappings</T>
        </h2>
        <span className="text-xs text-muted-foreground">
          <T context="workbook data sink mapped count">
            <Num>{mapped}</Num> of <Num>{targetColumns.length}</Num> mapped
          </T>
        </span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto h-7 text-xs"
          disabled={!editable || !hasSheet}
          onClick={() =>
            change(autoMapped(mappings, targetColumns, sheetColumns))
          }
        >
          <MagicWandIcon />
          <T context="workbook data sink auto-map button">Auto-Map by Name</T>
        </Button>
      </div>
      <div className="overflow-x-auto rounded border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="workbook data sink table header">Target column</T>
              </TableHead>
              <TableHead>
                <T context="workbook data sink table header">Value</T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {targetColumns.map((column) => (
              <MappingRow
                key={column.name}
                column={column}
                value={mappingFor(mappings, column.name)}
                sheetColumns={sheetColumns}
                editable={editable}
                invalid={issues.some(
                  (issue) =>
                    issue.targetColumn === column.name &&
                    issue.kind !== 'unknown-target',
                )}
                change={(value) =>
                  change(withMapping(mappings, column.name, value))
                }
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <SinkIssues issues={issues} />
    </section>
  );
}

/** One target column's mapping: its current value and what may change it. */
type MappingProps = {
  column: SinkTargetColumn;
  value: WorkbookSinkValue | undefined;
  sheetColumns: readonly string[];
  editable: boolean;
  invalid: boolean;
  change: (value: WorkbookSinkValue | undefined) => void;
};

function ValuePicker({
  column,
  value,
  sheetColumns,
  editable,
  invalid,
  change,
}: MappingProps) {
  const gt = useGT();
  const missing =
    value?.kind === 'column' && !sheetColumns.includes(value.column)
      ? value.column
      : undefined;
  return (
    <Select
      value={pickerValue(value)}
      disabled={!editable}
      onValueChange={(next) => {
        if (next === UNMAPPED) change(undefined);
        else if (next === CONSTANT)
          change(
            value?.kind === 'constant'
              ? value
              : { kind: 'constant', value: null },
          );
        else
          change({
            kind: 'column',
            column: next.slice(COLUMN_PREFIX.length),
          });
      }}
    >
      <SelectTrigger
        className="h-8 min-w-48 text-xs"
        aria-invalid={invalid}
        aria-label={gt('Value for {column}', {
          context: 'workbook data sink value picker',
          column: column.name,
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNMAPPED}>
          {column.isKey
            ? gt('Not mapped (required)', {
                context: 'workbook data sink unmapped key option',
              })
            : gt('Not mapped', {
                context: 'workbook data sink unmapped option',
              })}
        </SelectItem>
        <SelectSeparator />
        <SelectGroup>
          <SelectLabel>
            <T context="workbook data sink worksheet columns group">
              Worksheet column
            </T>
          </SelectLabel>
          {sheetColumns.map((name) => (
            <SelectItem key={name} value={COLUMN_PREFIX + name}>
              {name}
            </SelectItem>
          ))}
          {missing !== undefined && (
            <SelectItem value={COLUMN_PREFIX + missing}>
              {gt('{column} (missing)', {
                context: 'workbook data sink missing column option',
                column: missing,
              })}
            </SelectItem>
          )}
        </SelectGroup>
        <SelectSeparator />
        <SelectItem value={CONSTANT}>
          {gt('Constant value', {
            context: 'workbook data sink constant option',
          })}
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function MappingRow({
  column,
  value,
  sheetColumns,
  editable,
  invalid,
  change,
}: MappingProps) {
  const gt = useGT();
  return (
    <TableRow>
      <TableCell className="align-middle">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{column.name}</span>
          <span className="text-xs text-muted-foreground">{column.type}</span>
          {column.isKey && (
            <Badge variant="secondary">
              <T context="workbook data sink key column badge">Key</T>
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-2">
          <ValuePicker
            column={column}
            value={value}
            sheetColumns={sheetColumns}
            editable={editable}
            invalid={invalid}
            change={change}
          />
          {value?.kind === 'constant' && (
            <Input
              className="h-8 w-48 text-xs"
              value={value.value ?? ''}
              maxLength={1000}
              disabled={!editable}
              placeholder={gt('NULL', {
                context: 'workbook data sink empty constant placeholder',
              })}
              aria-label={gt('Constant for {column}', {
                context: 'workbook data sink constant input',
                column: column.name,
              })}
              onChange={(event) =>
                change({
                  kind: 'constant',
                  value: event.target.value === '' ? null : event.target.value,
                })
              }
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function SinkIssues({ issues }: { issues: readonly SinkIssue[] }) {
  if (!issues.length) return null;
  return (
    <ul role="alert" className="flex flex-col gap-0.5 text-xs text-destructive">
      {issues.map((issue) => (
        <li key={`${issue.kind}:${issue.targetColumn}`}>
          {issue.kind === 'missing-key' && (
            <T context="workbook data sink issue">
              <Var>{issue.targetColumn}</Var> is a key column and needs a value.
            </T>
          )}
          {issue.kind === 'unknown-column' && (
            <T context="workbook data sink issue">
              <Var>{issue.targetColumn}</Var> reads <Var>{issue.column}</Var>,
              which the worksheet does not have.
            </T>
          )}
          {issue.kind === 'unknown-target' && (
            <T context="workbook data sink issue">
              <Var>{issue.targetColumn}</Var> is not a column of the target
              table.
            </T>
          )}
        </li>
      ))}
    </ul>
  );
}
